import { Inject, Injectable } from "@angular/core";
import {
  Auth,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  User,
} from "@angular/fire/auth";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { CompanyService } from "./company.service";
import { UserService } from "./user.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private userSubject: BehaviorSubject<User | null> =
    new BehaviorSubject<User | null>(null);
  public user$: Observable<User | null> = this.userSubject.asObservable();

  constructor(
    private afAuth: Auth,
    @Inject(UserService) private userService: UserService,
    @Inject(CompanyService) private companyService: CompanyService,
    @Inject(Router) private router: Router
  ) {
    onAuthStateChanged(this.afAuth, (user) => {
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        this.userSubject.next(user);
      } else {
        localStorage.clear();
      }
    });
  }

  async login(user: any) {
    const auth = await signInWithEmailAndPassword(
      this.afAuth,
      user.email,
      user.password
    );

    const userRecord = await this.userService.getByUid(auth.user!.uid);
    if (!userRecord) {
      await this.afAuth.signOut();
      throw Object.assign(new Error("Usuário não encontrado no banco de dados."), {
        code: "user/not-found",
      });
    }

    const companyId: string = userRecord["companyId"];
    const company = await this.companyService.getCompanyById(companyId);
    if (!company) {
      await this.afAuth.signOut();
      throw Object.assign(new Error("Empresa não encontrada no banco de dados."), {
        code: "company/not-found",
      });
    }

    // Garante vínculo do lojista com a empresa (necessário para reviewPurchase).
    const uid = auth.user!.uid;
    if (
      company.uid !== uid &&
      company.ownerUid !== uid &&
      company.userId !== uid
    ) {
      await this.companyService.update(companyId, { uid });
    }

    localStorage.setItem("_companyId", companyId);
    localStorage.setItem("_cnpj", company.cnpj ?? "");

    const hasGeo = !!(
      company.geolocalizacao?.lat &&
      company.geolocalizacao?.lng
    );
    const hasCategory = !!(company.categoria && String(company.categoria).trim());

    localStorage.setItem("_hasGeo", String(hasGeo));
    localStorage.setItem("_hasCategory", String(hasCategory));

    // Redireciona para o perfil se geo ou categoria ainda não foram configuradas
    const profileComplete = hasGeo && hasCategory;
    this.router.navigate([profileComplete ? "/dashboard" : "/company-profile"]);
  }

  getAuth() {
    return this.afAuth;
  }

  logout() {
    localStorage.removeItem("_cnpj");
    localStorage.removeItem("_companyId");
    localStorage.removeItem("user");
    this.afAuth.signOut();
  }

  async sendFirstAccessEmail(email: string, document: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();

    const company = await this.companyService.getCompanyByEmailAndDocument(normalizedEmail, document);
    if (!company) {
      const err = new Error("Cadastro não encontrado. Verifique o e-mail e o CPF/CNPJ informados.");
      (err as any).code = "COMPANY_NOT_FOUND";
      throw err;
    }

    // Se o lojista veio do BotConversa, ele pode não ter conta no Firebase Auth ainda.
    // Tentamos criar — se já existir, ignoramos o erro e apenas enviamos o reset.
    try {
      const tempPassword = this.generateTempPassword();
      const credential = await createUserWithEmailAndPassword(this.afAuth, normalizedEmail, tempPassword);

      await this.userService.postAdd({
        uid: credential.user.uid,
        email: normalizedEmail,
        companyId: company.id,
      });

      await this.companyService.update(company.id, {
        uid: credential.user.uid,
      });

      // Desloga imediatamente — não queremos sessão aberta durante o primeiro acesso
      await this.afAuth.signOut();
    } catch (createError: any) {
      if (createError.code !== "auth/email-already-in-use") {
        throw createError;
      }
      // Conta já existente — segue para o envio do reset normalmente
    }

    await sendPasswordResetEmail(this.afAuth, normalizedEmail);
  }

  private generateTempPassword(): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    return Array.from({ length: 20 }, () =>
      chars[Math.floor(Math.random() * chars.length)]
    ).join("");
  }

  async registerUser(email: string, password: string, userData: any) {
    try {
      // Criar usuário no Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        this.afAuth,
        email,
        password
      );
      const userId = userCredential.user?.uid;

      if (userId) {
        await this.userService.postAdd({
          uid: userId,
          email: email,
          ...userData,
        });
        await sendPasswordResetEmail(this.afAuth, email);
      }
    } catch (error: any) {
      throw new Error(error.message);
    }
  }
}
